import { useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorDetail } from "./../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

const TEMPLATE = "ticker,shares,buy_price,buy_date\nAAPL,10,220.50,2024-05-01\nSHEL.L,50,2650,2024-06-15";

export const CsvImportDialog = ({ open, onOpenChange, portfolioId, onImported }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const reset = (o) => {
    if (!o) { setFile(null); setReport(null); }
    onOpenChange(o);
  };

  const submit = async () => {
    if (!file) return;
    setLoading(true);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/portfolios/${portfolioId}/import-csv`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      if (data.imported > 0) {
        toast.success(data.message);
        onImported();
      } else {
        toast.error("No rows imported — check the errors below");
      }
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="rounded-sm bg-card border-border sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Import holdings from CSV</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Columns: ticker, shares, buy_price, buy_date (YYYY-MM-DD). Buy price in native quote units — pence for London (.L) tickers.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <pre className="bg-background border border-border rounded-sm p-3 text-[11px] font-mono text-muted-foreground overflow-x-auto"
            data-testid="csv-template-example">{TEMPLATE}</pre>
          <input type="file" accept=".csv,text/csv" data-testid="csv-file-input"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); }}
            className="block w-full text-sm text-muted-foreground file:mr-3 file:px-3 file:py-2 file:rounded-sm file:border-0 file:bg-primary file:text-white file:text-xs file:cursor-pointer hover:file:bg-primary/85" />
          <Button onClick={submit} disabled={!file || loading}
            className="w-full rounded-sm bg-primary hover:bg-primary/85 text-white gap-2" data-testid="csv-import-submit">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <><Upload className="w-4 h-4" /> Import</>}
          </Button>
          {report && (
            <div className="text-sm fade-up" data-testid="csv-import-report">
              <p className="font-mono text-xs text-gain">{report.message}</p>
              {report.errors.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1" data-testid="csv-import-errors">
                  {report.errors.map((e, i) => (
                    <p key={i} className="text-xs text-loss font-mono">Row {e.row}: {e.error}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
